import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2, Upload, FileText } from 'lucide-react';
import RichTextEditor from './RichTextEditor';

// Helper to strip HTML tags for character counting
const stripHtmlTags = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

// Renders a single field based on its type
const DynamicField = ({ field, value, onChange, disabled = false, allFormData = {}, onFileUpload, onFileRemove, fieldNote }) => {
  const handleChange = (newValue) => {
    onChange(field.id, newValue);
  };

  const fieldId = `field-${field.id}`;
  
  // Check conditional visibility
  const isVisible = useMemo(() => {
    if (!field.conditional) return true;
    
    const { field_id, operator, value: conditionValue } = field.conditional;
    const dependentValue = allFormData[field_id];
    
    switch (operator) {
      case 'equals':
        return dependentValue === conditionValue;
      case 'not_equals':
        return dependentValue !== conditionValue;
      case 'not_empty':
        return dependentValue !== undefined && dependentValue !== null && dependentValue !== '';
      case 'is_empty':
        return dependentValue === undefined || dependentValue === null || dependentValue === '';
      case 'contains':
        return typeof dependentValue === 'string' && dependentValue.toLowerCase().includes((conditionValue || '').toLowerCase());
      default:
        return true;
    }
  }, [field.conditional, allFormData]);
  
  // Don't render if not visible
  if (!isVisible) return null;

  switch (field.type) {
    case 'text':
    case 'email':
      return (
        <div className="space-y-2">
          <Label htmlFor={fieldId}>
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </Label>
          <Input
            id={fieldId}
            type={field.type}
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={field.placeholder}
            disabled={disabled}
            className="rounded-sm"
            data-testid={`input-${field.id}`}
          />
          {field.help_text && <p className="text-xs text-slate-500">{field.help_text}</p>}
        </div>
      );

    case 'number':
      return (
        <div className="space-y-2">
          <Label htmlFor={fieldId}>
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </Label>
          <Input
            id={fieldId}
            type="number"
            value={value ?? ''}
            onChange={(e) => {
              const val = e.target.value;
              handleChange(val === '' ? '' : parseInt(val, 10));
            }}
            placeholder={field.placeholder}
            disabled={disabled}
            min={field.min_value ?? undefined}
            max={field.max_value ?? undefined}
            className="rounded-sm"
            data-testid={`number-${field.id}`}
          />
          {(field.min_value !== null || field.max_value !== null) && (
            <p className="text-xs text-slate-500">
              {field.min_value !== null && field.max_value !== null 
                ? `Value must be between ${field.min_value} and ${field.max_value}`
                : field.min_value !== null 
                  ? `Minimum: ${field.min_value}`
                  : `Maximum: ${field.max_value}`}
            </p>
          )}
          {field.help_text && <p className="text-xs text-slate-500">{field.help_text}</p>}
        </div>
      );

    case 'textarea':
      return (
        <div className="space-y-2">
          <Label htmlFor={fieldId}>
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </Label>
          <Textarea
            id={fieldId}
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={field.placeholder}
            disabled={disabled}
            className="rounded-sm min-h-[100px]"
            data-testid={`textarea-${field.id}`}
          />
          {field.help_text && <p className="text-xs text-slate-500">{field.help_text}</p>}
        </div>
      );

    case 'richtext':
      const charCount = stripHtmlTags(value || '').length;
      const minChars = field.min_chars;
      const maxChars = field.max_chars;
      const isUnderMin = minChars !== null && minChars !== undefined && charCount < minChars;
      const isOverMax = maxChars !== null && maxChars !== undefined && charCount > maxChars;
      
      return (
        <div className="space-y-2">
          <Label htmlFor={fieldId}>
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </Label>
          <RichTextEditor
            id={fieldId}
            value={value || ''}
            onChange={(content) => handleChange(content)}
            placeholder={field.placeholder || 'Enter your response...'}
            disabled={disabled}
          />
          {/* Character counter */}
          {(minChars !== null || maxChars !== null) && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {minChars !== null && (
                  <span className={`text-xs ${isUnderMin ? 'text-amber-600' : 'text-slate-500'}`}>
                    Min: {minChars} chars
                  </span>
                )}
                {minChars !== null && maxChars !== null && <span className="text-slate-300">|</span>}
                {maxChars !== null && (
                  <span className={`text-xs ${isOverMax ? 'text-red-600' : 'text-slate-500'}`}>
                    Max: {maxChars} chars
                  </span>
                )}
              </div>
              <span className={`text-xs font-medium ${
                isOverMax ? 'text-red-600' : isUnderMin ? 'text-amber-600' : 'text-slate-600'
              }`}>
                {charCount} characters
              </span>
            </div>
          )}
          {field.help_text && <p className="text-xs text-slate-500">{field.help_text}</p>}
        </div>
      );

    case 'date':
      return (
        <div className="space-y-2">
          <Label htmlFor={fieldId}>
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </Label>
          <Input
            id={fieldId}
            type="date"
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
            disabled={disabled}
            className="rounded-sm"
            data-testid={`date-${field.id}`}
          />
          {field.help_text && <p className="text-xs text-slate-500">{field.help_text}</p>}
        </div>
      );

    case 'select':
    case 'dropdown_list':
      return (
        <div className="space-y-2">
          <Label htmlFor={fieldId}>
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </Label>
          <Select
            value={value || ''}
            onValueChange={handleChange}
            disabled={disabled}
          >
            <SelectTrigger className="rounded-sm" data-testid={`select-${field.id}`}>
              <SelectValue placeholder={field.placeholder || 'Select an option...'} />
            </SelectTrigger>
            <SelectContent>
              {(field.options || []).map((option, idx) => (
                <SelectItem key={idx} value={option}>{option}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {field.help_text && <p className="text-xs text-slate-500">{field.help_text}</p>}
        </div>
      );

    case 'radio':
      return (
        <div className="space-y-2">
          <Label>
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </Label>
          <RadioGroup
            value={value || ''}
            onValueChange={handleChange}
            disabled={disabled}
            className="flex flex-wrap gap-4"
          >
            {(field.options || []).map((option, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <RadioGroupItem 
                  value={option} 
                  id={`${fieldId}-${idx}`} 
                  data-testid={`radio-${field.id}-${idx}`}
                />
                <label htmlFor={`${fieldId}-${idx}`} className="text-sm">{option}</label>
              </div>
            ))}
          </RadioGroup>
          {field.help_text && <p className="text-xs text-slate-500">{field.help_text}</p>}
        </div>
      );

    case 'checkbox':
      return (
        <div className="flex items-center gap-2">
          <Checkbox
            id={fieldId}
            checked={value || false}
            onCheckedChange={handleChange}
            disabled={disabled}
            data-testid={`checkbox-${field.id}`}
          />
          <label htmlFor={fieldId} className="text-sm">
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </label>
          {field.help_text && <p className="text-xs text-slate-500 ml-6">{field.help_text}</p>}
        </div>
      );

    case 'checkbox_group':
      const selectedValues = Array.isArray(value) ? value : [];
      return (
        <div className="space-y-2">
          <Label>
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </Label>
          <div className="flex flex-wrap gap-4">
            {(field.options || []).map((option, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <Checkbox
                  id={`${fieldId}-${idx}`}
                  checked={selectedValues.includes(option)}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      handleChange([...selectedValues, option]);
                    } else {
                      handleChange(selectedValues.filter(v => v !== option));
                    }
                  }}
                  disabled={disabled}
                  data-testid={`checkbox-group-${field.id}-${idx}`}
                />
                <label htmlFor={`${fieldId}-${idx}`} className="text-sm">{option}</label>
              </div>
            ))}
          </div>
          {field.help_text && <p className="text-xs text-slate-500">{field.help_text}</p>}
        </div>
      );

    case 'file':
      const displayName = typeof value === 'string'
        ? value
        : value?.name || value?.original_name || value?.filename || '';
      return (
        <div className="space-y-2">
          <Label>
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </Label>
          <div className="border-2 border-dashed border-slate-300 rounded-sm p-4 hover:border-slate-400 transition-colors">
            {value ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-slate-500" />
                  <span className="text-sm text-slate-700">{displayName || 'Uploaded file'}</span>
                </div>
                {!disabled && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => (onFileRemove ? onFileRemove(field.id, value) : handleChange(null))}
                    className="text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ) : (
              <label className="cursor-pointer flex flex-col items-center gap-2">
                <Upload className="w-8 h-8 text-slate-400" />
                <span className="text-sm text-slate-500">Click to upload or drag and drop</span>
                {field.accept_file_types && (
                  <span className="text-xs text-slate-400">Accepted: {field.accept_file_types}</span>
                )}
                <input
                  type="file"
                  className="hidden"
                  accept={field.accept_file_types || '*'}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      if (onFileUpload) {
                        onFileUpload(field.id, file);
                      } else {
                        handleChange(file.name);
                      }
                    }
                  }}
                  disabled={disabled}
                  data-testid={`file-${field.id}`}
                />
              </label>
            )}
          </div>
          {field.help_text && <p className="text-xs text-slate-500">{field.help_text}</p>}
          <p className="text-xs text-slate-400">Max 5 MB &middot; Accepted formats: PDF, XLS, DOCX, JPG, PNG</p>
          {fieldNote && <p className="text-xs text-amber-600">{fieldNote}</p>}
        </div>
      );

    case 'array':
      const items = Array.isArray(value) ? value : [];
      return (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <Label>{field.label}</Label>
              {field.help_text && <p className="text-xs text-slate-500">{field.help_text}</p>}
            </div>
            {!disabled && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleChange([...items, { name: '', date: '' }])}
                className="rounded-sm"
                data-testid={`add-${field.id}-btn`}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add
              </Button>
            )}
          </div>
          {items.map((item, idx) => (
            <div key={idx} className="flex gap-3 items-center p-3 bg-slate-50 rounded-sm">
              <Input
                placeholder="Name/Description"
                value={item.name || ''}
                onChange={(e) => {
                  const newItems = [...items];
                  newItems[idx] = { ...newItems[idx], name: e.target.value };
                  handleChange(newItems);
                }}
                disabled={disabled}
                className="rounded-sm flex-1"
                data-testid={`input-${field.id}-name-${idx}`}
              />
              <Input
                placeholder="Date"
                value={item.date || ''}
                onChange={(e) => {
                  const newItems = [...items];
                  newItems[idx] = { ...newItems[idx], date: e.target.value };
                  handleChange(newItems);
                }}
                disabled={disabled}
                className="rounded-sm w-32"
                data-testid={`input-${field.id}-date-${idx}`}
              />
              {!disabled && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleChange(items.filter((_, i) => i !== idx))}
                  className="text-red-500"
                  data-testid={`remove-${field.id}-btn-${idx}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          ))}
          {items.length === 0 && (
            <p className="text-sm text-slate-400 text-center py-2">No items added</p>
          )}
        </div>
      );

    case 'repeatable_group':
      const groupItems = Array.isArray(value) ? value : [];
      const subFields = field.sub_fields || [];
      
      // Create empty entry based on sub_fields
      const createEmptyEntry = () => {
        const entry = {};
        subFields.forEach(sf => {
          if (sf.type === 'checkbox_group') {
            entry[sf.id] = [];
          } else {
            entry[sf.id] = '';
          }
        });
        return entry;
      };

      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label>{field.label} {field.required && <span className="text-red-500">*</span>}</Label>
              {field.help_text && <p className="text-xs text-slate-500">{field.help_text}</p>}
            </div>
            {!disabled && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleChange([...groupItems, createEmptyEntry()])}
                className="rounded-sm"
                data-testid={`add-${field.id}-btn`}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Entry
              </Button>
            )}
          </div>
          
          {groupItems.map((item, idx) => (
            <div key={idx} className="p-4 bg-slate-50 rounded-sm border border-slate-200 space-y-3">
              <div className="flex items-center justify-between border-b border-slate-200 pb-2 mb-2">
                <span className="text-sm font-medium text-slate-600">Entry {idx + 1}</span>
                {!disabled && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleChange(groupItems.filter((_, i) => i !== idx))}
                    className="text-red-500 h-6"
                    data-testid={`remove-${field.id}-btn-${idx}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
              
              <div className="grid gap-3">
                {subFields.map((subField) => {
                  const subValue = item[subField.id];
                  
                  // Render sub-field based on its type
                  if (subField.type === 'text' || subField.type === 'email') {
                    return (
                      <div key={subField.id} className="space-y-1">
                        <Label className="text-sm">
                          {subField.label} {subField.required && <span className="text-red-500">*</span>}
                        </Label>
                        <Input
                          value={subValue || ''}
                          placeholder={subField.placeholder}
                          onChange={(e) => {
                            const newItems = [...groupItems];
                            newItems[idx] = { ...newItems[idx], [subField.id]: e.target.value };
                            handleChange(newItems);
                          }}
                          disabled={disabled}
                          className="rounded-sm"
                          data-testid={`input-${field.id}-${subField.id}-${idx}`}
                        />
                      </div>
                    );
                  }
                  
                  if (subField.type === 'checkbox_group') {
                    const selectedValues = Array.isArray(subValue) ? subValue : [];
                    return (
                      <div key={subField.id} className="space-y-2">
                        <Label className="text-sm">
                          {subField.label} {subField.required && <span className="text-red-500">*</span>}
                        </Label>
                        <div className="flex flex-wrap gap-3">
                          {(subField.options || []).map((option, optIdx) => (
                            <div key={optIdx} className="flex items-center gap-2">
                              <Checkbox
                                id={`${field.id}-${subField.id}-${idx}-${optIdx}`}
                                checked={selectedValues.includes(option)}
                                onCheckedChange={(checked) => {
                                  const newItems = [...groupItems];
                                  if (checked) {
                                    newItems[idx] = { 
                                      ...newItems[idx], 
                                      [subField.id]: [...selectedValues, option] 
                                    };
                                  } else {
                                    newItems[idx] = { 
                                      ...newItems[idx], 
                                      [subField.id]: selectedValues.filter(v => v !== option) 
                                    };
                                  }
                                  handleChange(newItems);
                                }}
                                disabled={disabled}
                                data-testid={`checkbox-${field.id}-${subField.id}-${idx}-${optIdx}`}
                              />
                              <label 
                                htmlFor={`${field.id}-${subField.id}-${idx}-${optIdx}`} 
                                className="text-sm"
                              >
                                {option}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  
                  if (subField.type === 'select') {
                    return (
                      <div key={subField.id} className="space-y-1">
                        <Label className="text-sm">
                          {subField.label} {subField.required && <span className="text-red-500">*</span>}
                        </Label>
                        <Select
                          value={subValue || ''}
                          onValueChange={(val) => {
                            const newItems = [...groupItems];
                            newItems[idx] = { ...newItems[idx], [subField.id]: val };
                            handleChange(newItems);
                          }}
                          disabled={disabled}
                        >
                          <SelectTrigger className="rounded-sm">
                            <SelectValue placeholder={subField.placeholder || 'Select...'} />
                          </SelectTrigger>
                          <SelectContent>
                            {(subField.options || []).map((opt, optIdx) => (
                              <SelectItem key={optIdx} value={opt}>{opt}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  }
                  
                  // Default to text input for unknown types
                  return (
                    <div key={subField.id} className="space-y-1">
                      <Label className="text-sm">{subField.label}</Label>
                      <Input
                        value={subValue || ''}
                        onChange={(e) => {
                          const newItems = [...groupItems];
                          newItems[idx] = { ...newItems[idx], [subField.id]: e.target.value };
                          handleChange(newItems);
                        }}
                        disabled={disabled}
                        className="rounded-sm"
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          
          {groupItems.length === 0 && (
            <div className="text-center py-4 bg-slate-50 rounded-sm border border-dashed border-slate-300">
              <p className="text-sm text-slate-400">No entries added yet</p>
              {!disabled && (
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  onClick={() => handleChange([createEmptyEntry()])}
                  className="text-greek-blue mt-1"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add first entry
                </Button>
              )}
            </div>
          )}
        </div>
      );

    default:
      return (
        <div className="space-y-2">
          <Label>{field.label}</Label>
          <Input
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={field.placeholder}
            disabled={disabled}
            className="rounded-sm"
          />
        </div>
      );
  }
};

// Renders an entire section with all its fields
const DynamicSection = ({ section, formData, onFieldChange, disabled = false, onFileUpload, onFileRemove, fieldNotes = {}, isFieldDisabled }) => {
  if (!section.enabled) return null;

  return (
    <div className="bg-white rounded-sm border border-slate-200 p-8 space-y-6">
      <div className="border-b border-slate-200 pb-4">
        <h2 className="font-heading text-2xl font-bold text-slate-900">{section.title}</h2>
        {section.description && (
          <p className="text-slate-500 mt-1">{section.description}</p>
        )}
      </div>

      <div className="space-y-6">
        {section.fields.map((field) => {
          const fieldDisabled = isFieldDisabled ? isFieldDisabled(field) : disabled;
          return (
            <DynamicField
              key={field.id}
              field={field}
              value={formData[field.id]}
              onChange={onFieldChange}
              disabled={fieldDisabled}
              allFormData={formData}
              onFileUpload={onFileUpload}
              onFileRemove={onFileRemove}
              fieldNote={fieldNotes[field.id]}
            />
          );
        })}
      </div>
    </div>
  );
};

// Main component that renders the entire form based on form_config
const DynamicFormRenderer = ({ 
  formConfig, 
  formData, 
  onFormDataChange, 
  currentSection,
  disabled = false,
  previewMode = false,
  onFileUpload,
  onFileRemove,
  fieldNotes = {},
  isFieldDisabled
}) => {
  // Get only enabled sections
  const enabledSections = formConfig.filter(s => s.enabled && s.fields.length > 0);
  
  // Find current section
  const section = enabledSections[currentSection] || enabledSections[0];

  if (!section) {
    return (
      <div className="bg-white rounded-sm border border-slate-200 p-8 text-center">
        <p className="text-slate-500">No form sections configured</p>
      </div>
    );
  }

  const handleFieldChange = (fieldId, value) => {
    onFormDataChange({
      ...formData,
      [fieldId]: value
    });
  };

  return (
    <DynamicSection
      section={section}
      formData={formData}
      onFieldChange={handleFieldChange}
      disabled={disabled || previewMode}
      onFileUpload={onFileUpload}
      onFileRemove={onFileRemove}
      fieldNotes={fieldNotes}
      isFieldDisabled={isFieldDisabled}
    />
  );
};

export { DynamicFormRenderer, DynamicField, DynamicSection };
export default DynamicFormRenderer;
